import { rankItem } from '@tanstack/match-sorter-utils';
import type {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  SortingState,
} from '@tanstack/react-table';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';

import type { IStoreIncomingMasterList } from '../../../api/IncomingInvoice.interface';

// Define a custom fuzzy filter function that will apply ranking info to rows (using match-sorter utils)
const fuzzyFilter: FilterFn<unknown> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value);

  // Store the itemRank info
  addMeta({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

type Props = {
  items: IStoreIncomingMasterList[];
  columns: ColumnDef<IStoreIncomingMasterList>[];
};

export default function useTable({ items, columns }: Props) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const table = useReactTable({
    data: items,
    columns,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(), //client-side sorting
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,

    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    debugTable: false,
    filterFns: {
      CustomFilter: (row: any, columnId: string, filterValue: any) => {
        const value = row.getValue(columnId);
        return (value as string)
          .toString()
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      },
      BooleanFilter: (row: any, columnId: string, filterValue: any) => {
        const value = row.getValue(columnId);
        return (value as string)
          .toString()
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      },
      fuzzy: fuzzyFilter,
    } as any,
  });

  return table;
}
